import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  useColorModeValue,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Text,
  Flex,
  Collapse,
} from '@chakra-ui/react';

import { CheckCircleIcon, WarningIcon, HamburgerIcon } from '@chakra-ui/icons';
import { BsXCircle } from 'react-icons/bs';

interface NavSection {
  id: string;
  label: string;
  isValid?: boolean;
}

interface StickyNavigationProps {
  sections: NavSection[];
  activeSection?: string;
  onActiveChange?: (sectionId: string) => void;
  onNavigate?: (sectionId: string) => void;
}

const StickyNavigation = ({
  sections,
  activeSection = '',
  onActiveChange,
  onNavigate,
}: StickyNavigationProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const bgColor = useColorModeValue('brand.400', 'brand.400');
  const borderColor = useColorModeValue('brand.500', 'brand.500');
  const textColor = useColorModeValue('white', 'white');
  const hoverBgColor = useColorModeValue('brand.500', 'brand.500');
  const activeButtonBg = useColorModeValue('brand.600', 'brand.600');
  const activeButtonColor = useColorModeValue('white', 'white');
  const inactiveButtonColor = useColorModeValue(
    'whiteAlpha.800',
    'whiteAlpha.800',
  );
  const navContainerRef = useRef<HTMLDivElement>(null);
  const activeButtonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>(
    {},
  );
  const activeSectionObj = sections.find((s) => s.id === activeSection);
  const invalidSectionsCount = sections.filter(
    (s) => s.isValid === false,
  ).length;

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 350;
      let currentSection = '';
      let minDistance = Infinity;

      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { top } = element.getBoundingClientRect();
          const elementTop = top + window.scrollY;
          const distance = Math.abs(scrollPosition - elementTop);

          if (distance < minDistance && elementTop <= scrollPosition + 100) {
            minDistance = distance;
            currentSection = section.id;
          }
        }
      }

      if (currentSection && currentSection !== activeSection) {
        onActiveChange?.(currentSection);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [sections, activeSection, onActiveChange]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const yOffset = -350;
      const y =
        element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
      onNavigate?.(sectionId);
    }
  };

  const getStatusIcon = (section: NavSection) => {
    if (section.isValid === false) {
      return WarningIcon; // Caution icon
    }
    return CheckCircleIcon; // Passed icon
  };

  const renderNavButton = (section: NavSection, isInDropdown = false) => {
    const isActive = activeSection === section.id;

    if (isInDropdown) {
      return (
        <MenuItem
          key={section.id}
          onClick={() => scrollToSection(section.id)}
          bg={isActive ? activeButtonBg : 'transparent'}
          color={isActive ? activeButtonColor : textColor}
          _hover={{
            bg: hoverBgColor,
          }}
          icon={
            <Icon
              as={getStatusIcon(section)}
              boxSize={3}
              color={section.isValid === false ? 'orange.300' : 'green.300'}
            />
          }
        >
          {section.label}
        </MenuItem>
      );
    }

    return (
      <Button
        key={section.id}
        ref={(el) => {
          activeButtonRefs.current[section.id] = el;
        }}
        size="xs"
        height="28px"
        px={3}
        bg={isActive ? activeButtonBg : 'whiteAlpha.100'}
        color={isActive ? activeButtonColor : inactiveButtonColor}
        borderWidth="1px"
        borderColor={isActive ? 'transparent' : 'whiteAlpha.300'}
        onClick={() => scrollToSection(section.id)}
        fontWeight={isActive ? 'semibold' : 'normal'}
        fontSize="xs"
        borderRadius="full"
        leftIcon={
          <Icon
            as={getStatusIcon(section)}
            boxSize={3}
            color={section.isValid === false ? 'orange.300' : 'green.300'}
          />
        }
        _hover={{
          bg: isActive ? activeButtonBg : hoverBgColor,
          transform: 'translateY(-1px)',
          shadow: 'sm',
        }}
        transition="all 0.2s"
      >
        {section.label}
      </Button>
    );
  };

  return (
    <Box
      position="sticky"
      bg={bgColor}
      backdropFilter="blur(20px)"
      top="104px"
      zIndex={10}
      borderColor={borderColor}
      borderBottomRadius="md"
      boxShadow="sm"
      mb={4}
    >
      {/* Compact Header - Always visible */}
      <Flex
        align="center"
        justify="space-between"
        p={3}
        cursor="pointer"
        onClick={() => setIsExpanded(!isExpanded)}
        borderBottom={isExpanded ? '1px solid' : 'none'}
        borderColor={borderColor}
      >
        <Flex align="center" gap={3}>
          <Text fontSize="sm" fontWeight="semibold" color={textColor}>
            Navigation
          </Text>
          {activeSectionObj && (
            <Flex align="center" gap={2}>
              <Text fontSize="sm" color="whiteAlpha.600">
                •
              </Text>
              <Icon
                as={getStatusIcon(activeSectionObj)}
                boxSize={3}
                color={
                  activeSectionObj.isValid === false
                    ? 'orange.300'
                    : 'green.300'
                }
              />
              <Text fontSize="sm" color={textColor}>
                {activeSectionObj.label}
              </Text>
            </Flex>
          )}
          {invalidSectionsCount > 0 && (
            <Flex align="center" gap={1}>
              <Text fontSize="sm" color="whiteAlpha.600">
                •
              </Text>
              <Icon as={WarningIcon} boxSize={3} color="orange.300" />
              <Text fontSize="xs" color="orange.300">
                {invalidSectionsCount} issues
              </Text>
            </Flex>
          )}
        </Flex>
        <IconButton
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
          icon={isExpanded ? <BsXCircle /> : <HamburgerIcon />}
          size="xs"
          variant="ghost"
          color={textColor}
          _hover={{ bg: hoverBgColor }}
        />
      </Flex>

      {/* Expandable Navigation */}
      <Collapse in={isExpanded} animateOpacity>
        <Box p={3}>
          <Flex align="center" gap={2}>
            {/* Desktop Navigation - Hidden on small screens */}
            <Box
              display={{ base: 'none', lg: 'block' }}
              flex="1"
              ref={navContainerRef}
            >
              <Flex flexWrap="wrap" gap={2}>
                {sections.map((section) => renderNavButton(section))}
              </Flex>
            </Box>

            {/* Mobile Navigation - Show hamburger menu on small screens */}
            <Box display={{ base: 'block', lg: 'none' }} flex="1">
              <Menu>
                <MenuButton
                  as={IconButton}
                  aria-label="Navigation menu"
                  icon={<HamburgerIcon />}
                  size="sm"
                  variant="outline"
                  colorScheme="gray"
                />
                <MenuList maxH="400px" overflowY="auto">
                  <Text
                    px={3}
                    py={2}
                    fontSize="xs"
                    color="gray.500"
                    fontWeight="bold"
                  >
                    SECTIONS
                  </Text>
                  {sections.map((section) => renderNavButton(section, true))}
                </MenuList>
              </Menu>
            </Box>
          </Flex>
        </Box>
      </Collapse>
    </Box>
  );
};

export default StickyNavigation;
